import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Alert,
  Spinner
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';
import './NetworkErrorModal.css';

/**
 * Модальное окно для отображения сетевых ошибок и управления переподключением.
 * Показывается при критических ошибках сети (502, 503, 504, таймауты).
 *
 * @param {boolean} isOpen - Открыто ли модальное окно
 * @param {function} toggle - Функция закрытия модального окна
 * @param {string} errorMessage - Сообщение об ошибке
 * @param {boolean} canRetry - Можно ли повторить запрос
 * @param {function} onRetry - Колбэк для повторной попытки
 * @returns {JSX.Element} Компонент модального окна
 */
const NetworkErrorModal = ({
  isOpen,
  toggle,
  errorMessage,
  canRetry = true,
  onRetry
}) => {
  const { t } = useTranslation('errors');
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('online');

  // Дефолтное сообщение об ошибке с i18n
  const displayErrorMessage = errorMessage || t('network.error');

  useEffect(() => {
    // Проверяем статус подключения
    const updateConnectionStatus = () => {
      setConnectionStatus(navigator.onLine ? 'online' : 'offline');
    };

    // Слушаем изменения статуса подключения
    window.addEventListener('online', updateConnectionStatus);
    window.addEventListener('offline', updateConnectionStatus);

    // Устанавливаем начальный статус
    updateConnectionStatus();

    return () => {
      window.removeEventListener('online', updateConnectionStatus);
      window.removeEventListener('offline', updateConnectionStatus);
    };
  }, []);

  const handleRetry = async () => {
    if (!canRetry || isRetrying) return;

    setIsRetrying(true);
    setRetryCount(prev => prev + 1);

    try {
      if (onRetry) {
        await onRetry();
      }

      // Если повтор успешен, закрываем модальное окно
      toggle();
      setRetryCount(0);
    } catch (error) {
      logger.error('Error during retry:', error);
      // Модальное окно остается открытым для следующей попытки
    } finally {
      setIsRetrying(false);
    }
  };

  const handleReload = () => {
    window.location.reload();
  };

  const getConnectionStatusIcon = () => {
    if (connectionStatus === 'offline') {
      return <i className="mdi mdi-wifi-off text-danger"></i>;
    }
    return <i className="mdi mdi-wifi text-success"></i>;
  };

  const getConnectionStatusText = () => {
    return connectionStatus === 'offline'
      ? t('network.noInternet')
      : t('network.internetActive');
  };

  const getErrorIcon = () => {
    if (displayErrorMessage.includes('502') || displayErrorMessage.includes(t('http.502'))) {
      return <i className="mdi mdi-server-network-off nem-error-icon"></i>;
    }
    if (displayErrorMessage.includes('503') || displayErrorMessage.includes(t('http.503'))) {
      return <i className="mdi mdi-server-off nem-error-icon"></i>;
    }
    if (displayErrorMessage.includes('504') || displayErrorMessage.includes(t('http.504'))) {
      return <i className="mdi mdi-timer-off-outline nem-error-icon"></i>;
    }
    return <i className="mdi mdi-alert-network-outline nem-error-icon"></i>;
  };

  const getRetryButtonText = () => {
    if (isRetrying) return t('network.connecting');
    if (retryCount === 0) return t('network.retry');
    return t('network.retryWithCount', { count: retryCount });
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered backdrop="static">
      <ModalHeader>
        <div className="d-flex align-items-center">
          <i className="mdi mdi-alert-circle-outline text-warning me-2"></i>
          {t('network.connectionProblem')}
        </div>
      </ModalHeader>

      <ModalBody>
        <div className="text-center mb-4">
          {getErrorIcon()}
          <h5 className="mt-3 mb-2">{t('network.cannotConnect')}</h5>
          <p className="text-muted">{displayErrorMessage}</p>
        </div>

        <Alert color="info" className="mb-3">
          <div className="d-flex align-items-center">
            {getConnectionStatusIcon()}
            <span className="ms-2">{getConnectionStatusText()}</span>
          </div>
        </Alert>

        {retryCount > 2 && (
          <Alert color="warning">
            <i className="mdi mdi-information-outline me-2"></i>
            {t('network.cannotRestore')}
          </Alert>
        )}

        <div className="mt-3">
          <h6>{t('network.possibleCauses')}</h6>
          <ul className="text-muted">
            <li>{t('network.temporaryMaintenance')}</li>
            <li>{t('network.internetProblems')}</li>
            <li>{t('network.highLoad')}</li>
          </ul>
        </div>
      </ModalBody>

      <ModalFooter className="justify-content-between">
        <div className="text-muted">
          <small>
            {connectionStatus === 'offline' ? (
              t('network.checkConnection')
            ) : (
              t('network.autoReconnect')
            )}
          </small>
        </div>

        <div>
          <Button
            color="secondary"
            className="me-2"
            onClick={handleReload}
            disabled={isRetrying}
          >
            <i className="mdi mdi-reload me-2"></i>
            {t('network.refreshPage')}
          </Button>

          {canRetry && (
            <Button
              color="primary"
              onClick={handleRetry}
              disabled={isRetrying || connectionStatus === 'offline'}
            >
              {isRetrying ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {getRetryButtonText()}
                </>
              ) : (
                <>
                  <i className="mdi mdi-refresh me-2"></i>
                  {getRetryButtonText()}
                </>
              )}
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

NetworkErrorModal.propTypes = {
  /** Открыто ли модальное окно */
  isOpen: PropTypes.bool.isRequired,
  /** Функция закрытия модального окна */
  toggle: PropTypes.func.isRequired,
  /** Сообщение об ошибке */
  errorMessage: PropTypes.string,
  /** Можно ли повторить запрос */
  canRetry: PropTypes.bool,
  /** Колбэк для повторной попытки */
  onRetry: PropTypes.func,
};

export default NetworkErrorModal;
